<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '@/i18n'
import { useDashboardStore } from '@/stores/dashboard'
import DashWidget from '@/components/dashboard/DashWidget.vue'
import { GRID_COLS, GRID_ROW_PX, GRID_MARGIN } from '@/config/widgetSources'

const props = defineProps({
  section: { type: Object, required: true },
  index:   { type: Number, required: true },
  total:   { type: Number, required: true },
})

const emit = defineEmits(['add-widget'])

const { t } = useI18n()
const dashStore = useDashboardStore()

const editMode = computed(() => dashStore.editMode)

// Andere Sections fürs "Verschieben nach"-Select im Widget-Configurator
const sectionRefs = computed(() =>
  dashStore.sections.map(s => ({ id: s.id, title: s.title }))
)

// ── Titel inline editieren ────────────────────────────────────────────────────
const titleInputEl = ref(null)

function onTitleInput(e) {
  dashStore.updateSection(props.section.id, { title: e.target.value })
}

// ── Hintergrundfarbe ──────────────────────────────────────────────────────────
const showColors = ref(false)

const COLOR_SWATCHES = [
  '', '#3b82f6', '#8b5cf6', '#22c55e', '#f97316',
  '#ef4444', '#14b8a6', '#ec4899', '#64748b',
]

function setColor(c) {
  dashStore.updateSection(props.section.id, { color: c })
  showColors.value = false
}

const sectionStyle = computed(() => {
  if (!props.section.color) return {}
  return {
    background: `color-mix(in srgb, ${props.section.color} 9%, transparent)`,
    borderColor: `color-mix(in srgb, ${props.section.color} 30%, transparent)`,
  }
})

// ── Collapse / Reorder / Delete ───────────────────────────────────────────────
function toggleCollapse() {
  dashStore.updateSection(props.section.id, { collapsed: !props.section.collapsed })
}

// ── Widget-Events ─────────────────────────────────────────────────────────────
function onWidgetRemove(widgetId) {
  dashStore.removeWidget(props.section.id, widgetId)
}

function onWidgetUpdate(widgetId, patch) {
  dashStore.updateWidget(props.section.id, widgetId, patch)
}

function onWidgetMove(widgetId, targetSectionId) {
  dashStore.moveWidgetToSection(widgetId, props.section.id, targetSectionId)
}

function onWidgetClone(widgetId) {
  dashStore.cloneWidget(props.section.id, widgetId)
}
</script>

<template>
  <section
    class="dash-section"
    :class="{ 'has-frame': section.color || section.title || editMode }"
    :style="sectionStyle"
  >

    <!-- ── Section-Header ──────────────────────────────────────────── -->
    <div
      v-if="section.title || editMode"
      class="sec-header"
    >
      <button class="sec-collapse" :title="t('dashboard.collapse')" @click="toggleCollapse">
        <i :class="section.collapsed ? 'pi pi-angle-right' : 'pi pi-angle-down'" />
      </button>

      <input
        v-if="editMode"
        ref="titleInputEl"
        class="sec-title-input"
        :value="section.title"
        :placeholder="t('dashboard.sectionTitlePlaceholder')"
        maxlength="80"
        @input="onTitleInput"
      />
      <h2 v-else class="sec-title">{{ section.title }}</h2>

      <div v-if="editMode" class="sec-actions">
        <!-- Farb-Picker -->
        <div class="sec-color-wrap">
          <button
            class="sec-btn"
            :title="t('dashboard.sectionColor')"
            @click="showColors = !showColors"
          >
            <i class="pi pi-palette" />
          </button>
          <div v-if="showColors" class="sec-swatches">
            <button
              v-for="c in COLOR_SWATCHES"
              :key="c || 'none'"
              type="button"
              class="sec-swatch"
              :class="{ selected: (section.color ?? '') === c, none: !c }"
              :style="c ? { background: c } : {}"
              :title="c || t('dashboard.colorAuto')"
              @click="setColor(c)"
            >
              <i v-if="!c" class="pi pi-times" />
            </button>
          </div>
        </div>

        <button
          class="sec-btn"
          :disabled="index === 0"
          :title="t('dashboard.moveUp')"
          @click="dashStore.moveSection(section.id, -1)"
        >
          <i class="pi pi-arrow-up" />
        </button>
        <button
          class="sec-btn"
          :disabled="index === total - 1"
          :title="t('dashboard.moveDown')"
          @click="dashStore.moveSection(section.id, 1)"
        >
          <i class="pi pi-arrow-down" />
        </button>
        <button
          class="sec-btn"
          :title="t('dashboard.addWidget')"
          @click="emit('add-widget', section.id)"
        >
          <i class="pi pi-plus" />
        </button>
        <button
          class="sec-btn sec-btn--delete"
          :title="t('dashboard.deleteSection')"
          @click="dashStore.removeSection(section.id)"
        >
          <i class="pi pi-trash" />
        </button>
      </div>
    </div>

    <!-- ── Grid (v-show statt v-if: vue-grid-layouts resize-Listener
         dürfen beim Collapse nicht unmounted werden) ────────────────── -->
    <div v-show="!section.collapsed">
      <div v-if="section.layout.length === 0" class="sec-empty">
        <i class="pi pi-th-large" />
        <span>{{ t('dashboard.emptySection') }}</span>
        <pButton
          v-if="editMode"
          icon="pi pi-plus"
          :label="t('dashboard.addWidget')"
          size="small"
          outlined
          @click="emit('add-widget', section.id)"
        />
      </div>

      <grid-layout
        v-else
        :layout="section.layout"
        :col-num="GRID_COLS"
        :row-height="GRID_ROW_PX"
        :margin="[GRID_MARGIN, GRID_MARGIN]"
        :is-draggable="editMode"
        :is-resizable="editMode"
        :use-css-transforms="true"
        :auto-size="true"
        :vertical-compact="true"
        @layout-updated="dashStore.onLayoutUpdated"
      >
        <grid-item
          v-for="item in section.layout"
          :key="item.i"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :i="item.i"
          drag-allow-from=".dw-drag-handle"
        >
          <DashWidget
            :widget="item"
            :edit-mode="editMode"
            :sections="sectionRefs"
            :section-id="section.id"
            @remove="onWidgetRemove"
            @update="patch => onWidgetUpdate(item.i, patch)"
            @move-to="target => onWidgetMove(item.i, target)"
            @clone="onWidgetClone"
          />
        </grid-item>
      </grid-layout>
    </div>

  </section>
</template>

<style scoped>
.dash-section {
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background .15s, border-color .15s;
}
.dash-section.has-frame {
  padding: 4px 10px 10px;
}

/* ── Header ── */
.sec-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px 2px;
  min-height: 34px;
}

.sec-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: .75rem;
  color: var(--p-text-color-secondary);
  flex-shrink: 0;
}
.sec-collapse:hover { background: var(--p-surface-200, rgba(0,0,0,.08)); }

.sec-title {
  margin: 0;
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--p-text-color-secondary);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sec-title-input {
  flex: 1;
  min-width: 0;
  border: 1px dashed var(--p-content-border-color, rgba(0,0,0,.15));
  border-radius: 4px;
  padding: 3px 8px;
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  background: var(--p-surface-0, #fff);
  color: var(--p-text-color);
  outline: none;
}
.sec-title-input:focus { border-color: var(--p-primary-color); border-style: solid; }

/* ── Aktionen ── */
.sec-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.sec-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: .7rem;
  color: var(--p-text-color-secondary);
  transition: background .12s, color .12s;
}
.sec-btn:hover:not(:disabled) {
  background: var(--p-surface-200, rgba(0,0,0,.08));
  color: var(--p-text-color);
}
.sec-btn:disabled { opacity: .3; cursor: default; }
.sec-btn--delete:hover {
  background: var(--p-red-100, #fee2e2) !important;
  color: var(--p-red-600, #dc2626) !important;
}

/* ── Farb-Picker ── */
.sec-color-wrap { position: relative; }

.sec-swatches {
  position: absolute;
  top: 28px;
  right: 0;
  z-index: 50;
  display: flex;
  gap: 5px;
  padding: 8px;
  border-radius: 8px;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-content-border-color, rgba(0,0,0,.1));
  box-shadow: 0 4px 16px rgba(0,0,0,.12);
}

.sec-swatch {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform .1s, border-color .1s;
}
.sec-swatch:hover { transform: scale(1.15); }
.sec-swatch.selected { border-color: var(--p-text-color); }
.sec-swatch.none {
  background: var(--p-surface-100, #f1f5f9);
  color: var(--p-text-color-secondary);
  font-size: .55rem;
}

/* ── Empty state ── */
.sec-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 0;
  color: var(--p-text-color-secondary);
  font-size: .85rem;
}
.sec-empty i { font-size: 1.4rem; opacity: .4; }

/* ── Dark mode ── */
:global(.my-app-dark) .sec-title-input {
  background: var(--p-surface-900, #0f172a);
  border-color: var(--p-surface-700, #334155);
}
:global(.my-app-dark) .sec-swatches {
  background: var(--p-surface-800, #1e293b);
  border-color: var(--p-surface-700, #334155);
}
:global(.my-app-dark) .sec-swatch.none {
  background: var(--p-surface-700, #334155);
}
:global(.my-app-dark) .sec-collapse:hover,
:global(.my-app-dark) .sec-btn:hover:not(:disabled) {
  background: var(--p-surface-700, rgba(255,255,255,.08));
}
</style>
